import type React from 'react';
import type { ChangeEvent, KeyboardEvent, MutableRefObject } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useField } from 'react-final-form';

import Input, { type Props } from '../Input/Input';

import styles from './NumericPIN.module.scss';

const MAX_PIN_LENGTH = 6;

const NumericPIN: React.FC<Props> = (props) => {
  const { input } = useField(props.name || '');
  const [pin, setPin] = useState(Array(MAX_PIN_LENGTH).fill(''));
  const [nextInputToChange, setNextInputToChange] = useState<number>(0);
  const inputs: MutableRefObject<HTMLInputElement[]> = useRef([]);

  const numsOnly = (value: string) => value.replace(/[^\d]/g, '');

  const setNewValue = (value: string, index: number) => {
    const newPin = [...pin];
    newPin[index] = value;
    setPin(newPin);

    if (value === '') {
      setNextInputToChange(index);
    } else {
      setNextInputToChange(index + 1);
    }

    input.onChange(newPin.join(''));
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>, index: number) => {
    e.preventDefault();
    const { value } = e.target;

    if (/^\d$/.test(value)) {
      setNewValue(value, index);
    }
  };

  const handleFocus = (index: number) => {
    if (nextInputToChange >= MAX_PIN_LENGTH) {
      inputs.current[nextInputToChange - 1]?.focus();
      return;
    }

    if (nextInputToChange !== index) {
      inputs.current[nextInputToChange]?.focus();
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if ((event.code === 'Backspace' || event.code === 'Delete') && nextInputToChange > 0) {
      if (nextInputToChange >= MAX_PIN_LENGTH) {
        inputs.current[nextInputToChange - 1].value = '';
      }

      setNewValue('', nextInputToChange - 1);
    }
  };

  useEffect(() => {
    inputs.current[nextInputToChange]?.focus();
  }, [nextInputToChange]);

  return (
    <div className={styles.inputs} data-testid="numeric-pin">
      {pin.map((digit, index) => (
        <div
          data-testid={`numeric-pin-${index}`}
          key={`input-wrapper-${index}`}
          className={styles.inputWrapper}
        >
          <Input
            {...props}
            key={index}
            name={`${props.name || ''}-${index}`}
            showValidationTick={false}
            hideLabel={true}
            placeholder=""
            type="text"
            parse={numsOnly}
            maxLength={1}
            value={digit}
            onChange={(e) => handleChange(e, index)}
            onFocus={() => handleFocus(index)}
            onKeyDown={(e) => onKeyDown(e)}
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            inputRef={(el: HTMLInputElement) => (inputs.current[index] = el)}
          />
        </div>
      ))}
    </div>
  );
};

export default NumericPIN;
